import { ErrorBoundary, type MediaAssetType } from "@prismicio/editor-ui"
import { Suspense } from "react"

import { SelectionManagerProvider } from "../SelectionManager"
import { EmptyMediaList } from "./components/EmptyMediaList"
import { MediaLibraryActions } from "./components/MediaLibraryActions"
import {
  MediaLibraryProvider,
  useMediaLibrary,
} from "./components/MediaLibraryContext"
import { MediaLibraryDropzone } from "./components/MediaLibraryDropzone"
import { MediaLibraryRenderError } from "./components/MediaLibraryRenderError"
import { MediaLibrarySkeleton } from "./components/MediaLibrarySkeleton"
import { MediaLibraryToastProvider } from "./components/MediaLibraryToast"
import { MediaList } from "./components/MediaList"
import { Sidepane } from "./components/Sidepane"
import { Toolbar } from "./components/Toolbar"
import { useIsEmptyMediaLibrary } from "./hooks/useIsEmptyMediaLibrary"
import { MediaLibraryLayout } from "./MediaLibraryLayout"

interface MediaLibraryProps {
  /**
   * The type of media to limit the media library to.
   * If different than `all`, the media library will disable the asset type filter
   * @default all
   */
  assetType?: MediaAssetType
  /**
   * A container in which the actions (search bar & upload button) are rendered.
   * If not provided, the actions will be rendered inside the media library.
   */
  actionContainer?: Element | null
  onSelectedChange?: (ids: Set<string>) => void
}

export function MediaLibrary(props: MediaLibraryProps) {
  const { assetType, onSelectedChange, actionContainer } = props

  return (
    <ErrorBoundary renderError={() => <MediaLibraryRenderError />}>
      <Suspense
        fallback={<MediaLibrarySkeleton actionContainer={actionContainer} />}
      >
        <SelectionManagerProvider>
          <MediaLibraryProvider
            assetType={assetType}
            actionContainer={actionContainer}
            onSelectedChange={onSelectedChange}
          >
            <MediaLibraryDropzone>
              <MediaLibraryContent />
            </MediaLibraryDropzone>
          </MediaLibraryProvider>
        </SelectionManagerProvider>
      </Suspense>
    </ErrorBoundary>
  )
}

function MediaLibraryContent() {
  const { media } = useMediaLibrary()
  const isEmptyLibrary = useIsEmptyMediaLibrary()

  return (
    <MediaLibraryToastProvider>
      <MediaLibraryLayout
        sidePane={isEmptyLibrary ? undefined : <Sidepane />}
        mediaActions={<MediaLibraryActions />}
        mediaToolbar={<Toolbar />}
        mediaList={media.length === 0 ? <EmptyMediaList /> : <MediaList />}
      />
    </MediaLibraryToastProvider>
  )
}
